<template>
    <div class="ps-page-body">
        <SeoHead
            :title="data?.title || 'Contact'"
            description="Get in touch with the ProfileSuite team for support, licensing, and partnership questions."
        />

        <marketing-page-hero
            eyebrow="Get in touch"
            :title="__(data?.title || 'Contact us')"
            :lead="page.content_details || __('Questions about Print Studio, licensing, or deployment? Send us a message — we typically reply within one business day.')"
        >
            <template #chips>
                <a v-if="page.email" :href="`mailto:${page.email}`" class="ps-page-hero__chip">
                    <icon name="email" class="w-4 h-4" />
                    {{ page.email }}
                </a>
                <a v-if="page.phone" :href="`tel:${page.phone}`" class="ps-page-hero__chip">
                    <icon name="phone" class="w-4 h-4" />
                    {{ page.phone }}
                </a>
            </template>
        </marketing-page-hero>

        <section class="ps-page-body__section">
            <div class="container mx-auto">
                <div class="-mx-4 flex flex-wrap lg:justify-between">
                    <div class="w-full px-4 lg:w-1/2 xl:w-6/12">
                        <div class="mb-12 max-w-[570px] lg:mb-0">
                            <h2 class="text-slate-900 mb-6 text-2xl font-bold sm:text-3xl">
                                {{ page.content_text }}
                            </h2>
                            <div class="mb-8 flex w-full max-w-[370px]">
                                <div class="mr-6 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                                    <icon name="home" class="w-7 h-7" />
                                </div>
                                <div class="w-full">
                                    <h4 class="text-slate-900 mb-1 text-lg font-bold">{{ __('Our Location') }}</h4>
                                    <p class="text-slate-600 text-base" v-html="sanitizeHtml(page.location)" />
                                </div>
                            </div>
                            <div class="mb-8 flex w-full max-w-[370px]">
                                <div class="mr-6 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                                    <icon name="phone" class="w-7 h-7" />
                                </div>
                                <div class="w-full">
                                    <h4 class="text-slate-900 mb-1 text-lg font-bold">{{ __('Phone Number') }}</h4>
                                    <p class="text-slate-600 text-base">
                                        <a :href="'tel:' + page.phone" class="hover:text-emerald-700">{{ page.phone }}</a>
                                    </p>
                                </div>
                            </div>
                            <div class="mb-8 flex w-full max-w-[370px]">
                                <div class="mr-6 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                                    <icon name="email" class="w-7 h-7" />
                                </div>
                                <div class="w-full">
                                    <h4 class="text-slate-900 mb-1 text-lg font-bold">{{ __('Email Address') }}</h4>
                                    <p class="text-slate-600 text-base">
                                        <a :href="'mailto:' + page.email" class="hover:text-emerald-700">{{ page.email }}</a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="w-full px-4 lg:w-1/2 xl:w-5/12">
                        <div class="relative rounded-2xl border border-slate-200 bg-white p-8 shadow-lg sm:p-10">
                            <form method="post" @submit.prevent="store">
                                <div class="mb-6">
                                    <input
                                        v-model="form.name"
                                        type="text"
                                        placeholder="Your Name"
                                        class="w-full rounded-xl border border-slate-200 py-3 px-4 text-base text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                                        required
                                    >
                                </div>
                                <div class="mb-6">
                                    <input
                                        v-model="form.email"
                                        type="email"
                                        placeholder="Your Email"
                                        class="w-full rounded-xl border border-slate-200 py-3 px-4 text-base text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                                        required
                                    >
                                </div>
                                <div class="mb-6">
                                    <input
                                        v-model="form.phone"
                                        type="text"
                                        placeholder="Your Phone"
                                        class="w-full rounded-xl border border-slate-200 py-3 px-4 text-base text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                                    >
                                </div>
                                <div class="mb-6">
                                    <textarea
                                        v-model="form.message"
                                        rows="6"
                                        placeholder="Your Message"
                                        class="w-full resize-none rounded-xl border border-slate-200 py-3 px-4 text-base text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                                        required
                                    />
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        class="w-full cursor-pointer rounded-xl border border-emerald-600 bg-emerald-600 p-3.5 font-semibold text-white transition hover:bg-emerald-700"
                                    >
                                        {{ __('Send Message') }}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import Layout from '@/Shared/Landing/Layout'
import Icon from '@/Shared/Icon'
import MarketingPageHero from '@/Shared/Landing/MarketingPageHero'
import SeoHead from '@/Shared/SeoHead'
import sanitizeHtml from 'sanitize-html'

export default {
    layout: Layout,
    components: {
        Icon,
        MarketingPageHero,
        SeoHead,
    },
    props: {
        data: Object,
        title: String,
    },
    data() {
        return {
            page: JSON.parse(this.data.html),
            form: this.$inertia.form({
                name: '',
                email: '',
                phone: '',
                message: '',
            }),
        }
    },
    methods: {
        sanitizeHtml,
        store() {
            this.form.post(this.route('contact.send'), {
                onSuccess: () => {
                    this.form.reset()
                },
            })
        },
    },
}
</script>

<style scoped>
svg {
    fill: currentColor;
}
</style>
